import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "@/pages/layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import FieldHint from "@/components/laboral/FieldHint";
import { Checkbox } from "@/components/ui/checkbox";
import LaboralFlujoOperativo from "@/components/laboral/LaboralFlujoOperativo";
import { laborDefaultsFromClient, mergeClientDefaults } from "@/lib/clientLaboralProfile";
import { WORK_MODULES } from "@/modules/workModuleCore";
import { useModuleAccess } from "@/modules/useModuleAccess";
import ModulePlanBlockedDialog from "@/components/ModulePlanBlockedDialog";

const CONTEXT_LABEL = "Asistente Laboral y REPSE";

function emptyForm() {
  return {
    registro_patronal: "",
    razon_social_imss: "",
    prima_riesgo: "",
    nombre_sucursal_obra: "",
    usuario_idse: "",
    contrasena_idse: "",
    representante_legal: "",
    cargo_representante: "",
    domicilio_patron: "",
    municipio_patron: "",
    estado_patron: "",
    telefono_patron: "",
    correo_patron: "",
    actividad_patron: "",
  };
}

/**
 * Registros Patronales y Certificados — relación 1:N por RFC de cliente.
 * Sección superior: alta; sección inferior: tabla histórica de patrones activos.
 */
export default function RegistrosPatronales() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [form, setForm] = useState(emptyForm);
  const [pfxFile, setPfxFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [rfcCliente, setRfcCliente] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editPfx, setEditPfx] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [fieldHints, setFieldHints] = useState({});
  const [matrizConfigured, setMatrizConfigured] = useState(false);
  const [usarCertificadoMatriz, setUsarCertificadoMatriz] = useState(false);
  const pfxInputRef = useRef(null);
  const editPfxRef = useRef(null);
  const { guardCriticalAction, planBlockedOpen, setPlanBlockedOpen } = useModuleAccess(
    WORK_MODULES.ASISTENTE_LABORAL,
  );

  const cli = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId]);

  /** actualizar_tabla_patronal (cliente): consulta API y refresca el grid inferior. */
  const actualizarTablaPatronal = useCallback(async () => {
    if (!clientId) {
      setRows([]);
      setRfcCliente("");
      return;
    }
    setLoadingTable(true);
    try {
      const { data } = await api.get("/laboral/registros-patronales", {
        params: { client_id: clientId },
      });
      setRows(Array.isArray(data.items) ? data.items : []);
      setRfcCliente(data.rfc_cliente_principal || cli?.rfc || "");
    } catch (e) {
      setRows([]);
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar registros patronales");
    } finally {
      setLoadingTable(false);
    }
  }, [clientId, cli?.rfc]);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  useEffect(() => {
    api.get("/laboral/registros-patronales/field-hints")
      .then(({ data }) => setFieldHints(data?.field_hints || {}))
      .catch(() => setFieldHints({}));
  }, []);


  useEffect(() => {
    const base = emptyForm();
    setForm(cli ? mergeClientDefaults(base, laborDefaultsFromClient(cli)) : base);
  }, [clientId, cli?.id]);

  useEffect(() => {
    actualizarTablaPatronal();
  }, [actualizarTablaPatronal]);

  useEffect(() => {
    if (!clientId) {
      setMatrizConfigured(false);
      setUsarCertificadoMatriz(false);
      return;
    }
    api.get(`/clients/${clientId}/credentials`)
      .then(({ data }) => {
        const ok = Boolean(data?.imss_matriz_status?.configured);
        setMatrizConfigured(ok);
        setUsarCertificadoMatriz(ok);
      })
      .catch(() => {
        setMatrizConfigured(false);
        setUsarCertificadoMatriz(false);
      });
  }, [clientId]);

  const onField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onRegister = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente de trabajo");
      return;
    }
    if (!form.registro_patronal.trim()) {
      toast.error("Indique el registro patronal");
      return;
    }
    if (!form.razon_social_imss.trim()) {
      toast.error("Indique la razón social IMSS");
      return;
    }
    if (!usarCertificadoMatriz && !pfxFile) {
      toast.error("Debe cargar el archivo certificado .pfx o marcar «Usar certificado de matriz»");
      return;
    }
    if (usarCertificadoMatriz && !matrizConfigured) {
      toast.error("Configure el certificado IMSS matriz en Clientes → Certificados y FIEL");
      return;
    }

    guardCriticalAction(async () => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("registro_patronal", form.registro_patronal.trim());
      body.append("razon_social_imss", form.razon_social_imss.trim());
      body.append("prima_riesgo", form.prima_riesgo.trim());
      body.append("nombre_sucursal_obra", form.nombre_sucursal_obra.trim());
      body.append("usuario_idse", form.usuario_idse.trim());
      body.append("contrasena_idse", form.contrasena_idse);
      body.append("usar_certificado_matriz", usarCertificadoMatriz ? "true" : "false");
      body.append("representante_legal", form.representante_legal.trim());
      body.append("cargo_representante", form.cargo_representante.trim());
      body.append("domicilio_patron", form.domicilio_patron.trim());
      body.append("municipio_patron", form.municipio_patron.trim());
      body.append("estado_patron", form.estado_patron.trim());
      body.append("telefono_patron", form.telefono_patron.trim());
      body.append("correo_patron", form.correo_patron.trim());
      body.append("actividad_patron", form.actividad_patron.trim());
      if (pfxFile) {
        body.append("certificado_pfx", pfxFile);
      }

      await api.post("/laboral/registros-patronales", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Registro patronal guardado");
      setForm(cli ? mergeClientDefaults(emptyForm(), laborDefaultsFromClient(cli)) : emptyForm());
      setPfxFile(null);
      if (pfxInputRef.current) pfxInputRef.current.value = "";
      await actualizarTablaPatronal();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al registrar patrón");
    } finally {
      setBusy(false);
    }
    });
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      registro_patronal: row.registro_patronal || "",
      razon_social_imss: row.razon_social_imss || "",
      prima_riesgo: row.prima_riesgo || "",
      nombre_sucursal_obra: row.nombre_sucursal_obra || "",
      usuario_idse: row.usuario_idse || "",
      contrasena_idse: "",
      representante_legal: row.representante_legal || "",
      cargo_representante: row.cargo_representante || "",
      domicilio_patron: row.domicilio_patron || "",
      municipio_patron: row.municipio_patron || "",
      estado_patron: row.estado_patron || "",
      telefono_patron: row.telefono_patron || "",
      correo_patron: row.correo_patron || "",
      actividad_patron: row.actividad_patron || "",
    });
    setEditPfx(null);
  };

  const onSaveEdit = async () => {
    if (!editRow?.id || !clientId) return;
    guardCriticalAction(async () => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("client_id", clientId);
      body.append("razon_social_imss", editForm.razon_social_imss.trim());
      body.append("prima_riesgo", editForm.prima_riesgo.trim());
      body.append("nombre_sucursal_obra", editForm.nombre_sucursal_obra.trim());
      body.append("usuario_idse", editForm.usuario_idse.trim());
      body.append("representante_legal", editForm.representante_legal.trim());
      body.append("cargo_representante", editForm.cargo_representante.trim());
      body.append("domicilio_patron", editForm.domicilio_patron.trim());
      body.append("municipio_patron", editForm.municipio_patron.trim());
      body.append("estado_patron", editForm.estado_patron.trim());
      body.append("telefono_patron", editForm.telefono_patron.trim());
      body.append("correo_patron", editForm.correo_patron.trim());
      body.append("actividad_patron", editForm.actividad_patron.trim());
      if (editForm.contrasena_idse) {
        body.append("contrasena_idse", editForm.contrasena_idse);
      }
      if (editPfx) {
        body.append("certificado_pfx", editPfx);
      }

      await api.put(`/laboral/registros-patronales/${editRow.id}`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Registro actualizado");
      setEditRow(null);
      await actualizarTablaPatronal();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al actualizar");
    } finally {
      setBusy(false);
    }
    });
  };

  const onConfirmDelete = async () => {
    if (!deleteRow?.id || !clientId) return;
    guardCriticalAction(async () => {
    setBusy(true);
    try {
      await api.delete(`/laboral/registros-patronales/${deleteRow.id}`, {
        params: { client_id: clientId },
      });
      toast.success("Registro patronal eliminado");
      setDeleteRow(null);
      await actualizarTablaPatronal();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al eliminar");
    } finally {
      setBusy(false);
    }
    });
  };

  return (
    <div className="space-y-6" data-testid="registros-patronales-page">
      <PageHeader
        title="Registros Patronales y Certificados"
        subtitle={CONTEXT_LABEL}
        description="Paso 2 del flujo: registre matriz y obras (registro patronal, certificado .pfx e IDSE)."
      />

      <LaboralFlujoOperativo>
      <div className="space-y-6">
      {!clientId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Seleccione un cliente de trabajo en el Panel General para registrar patrones.
        </div>
      )}

      {/* Sección superior — formulario de alta */}
      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Building2 className="h-4 w-4" />
          Alta de registro patronal
          {rfcCliente ? (
            <span className="ml-auto font-mono text-xs text-foreground">RFC: {rfcCliente}</span>
          ) : null}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="rp-registro">Registro Patronal</Label>
            <FieldHint>{fieldHints.registro_patronal}</FieldHint>
            <Input
              id="rp-registro"
              value={form.registro_patronal}
              onChange={onField("registro_patronal")}
              placeholder="Ej. Y1234567890"
              disabled={!clientId || busy}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="rp-razon">Razón Social IMSS</Label>
            <FieldHint>{fieldHints.razon_social_imss}</FieldHint>
            <Input
              id="rp-razon"
              value={form.razon_social_imss}
              onChange={onField("razon_social_imss")}
              disabled={!clientId || busy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rp-prima">Prima de Riesgo</Label>
            <FieldHint>{fieldHints.prima_riesgo}</FieldHint>
            <Input
              id="rp-prima"
              value={form.prima_riesgo}
              onChange={onField("prima_riesgo")}
              placeholder="Ej. 0.54355"
              disabled={!clientId || busy}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="rp-sucursal">Nombre de Sucursal / Obra</Label>
            <FieldHint>{fieldHints.nombre_sucursal_obra}</FieldHint>
            <Input
              id="rp-sucursal"
              value={form.nombre_sucursal_obra}
              onChange={onField("nombre_sucursal_obra")}
              disabled={!clientId || busy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rp-usuario">Usuario IDSE</Label>
            <FieldHint>{fieldHints.usuario_idse}</FieldHint>
            <Input
              id="rp-usuario"
              value={form.usuario_idse}
              onChange={onField("usuario_idse")}
              disabled={!clientId || busy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rp-clave">Contraseña IDSE</Label>
            <FieldHint>{fieldHints.contrasena_idse}</FieldHint>
            <Input
              id="rp-clave"
              type="password"
              value={form.contrasena_idse}
              onChange={onField("contrasena_idse")}
              disabled={!clientId || busy}
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-2 md:col-span-2 lg:col-span-3 rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="rp-usar-matriz"
                checked={usarCertificadoMatriz}
                onCheckedChange={(v) => setUsarCertificadoMatriz(Boolean(v))}
                disabled={!clientId || busy || !matrizConfigured}
                data-testid="rp-usar-certificado-matriz"
              />
              <div className="space-y-1">
                <Label htmlFor="rp-usar-matriz" className="text-sm cursor-pointer">
                  Usar certificado IMSS de la matriz (Clientes)
                </Label>
                <p className="text-[11px] text-muted-foreground m-0">
                  {matrizConfigured
                    ? "Reutiliza el .pfx e IDSE configurados en Certificados y FIEL. Indique sucursal/obra y registro patronal propios."
                    : (
                      <>
                        No hay certificado matriz. Configúrelo en{" "}
                        <Link to="/clientes?tab=credenciales" className="underline text-[#0F2D5C] font-medium">
                          Clientes → Certificados y FIEL
                        </Link>
                        .
                      </>
                    )}
                </p>
              </div>
            </div>
            {!usarCertificadoMatriz ? (
              <div className="space-y-2">
                <Label htmlFor="rp-pfx">Certificado (.pfx) propio de esta obra/sucursal</Label>
                <FieldHint>{fieldHints.certificado_pfx}</FieldHint>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="rp-pfx"
                    ref={pfxInputRef}
                    type="file"
                    accept=".pfx"
                    disabled={!clientId || busy}
                    onChange={(e) => setPfxFile(e.target.files?.[0] || null)}
                    className="max-w-md"
                  />
                  {pfxFile ? (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Upload className="h-3 w-3" />
                      {pfxFile.name}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-[11px] text-emerald-800 m-0">
                Este patrón usará el certificado centralizado de matriz (sin duplicar el archivo).
              </p>
            )}
          </div>
        </div>

        <details className="rounded-md border border-slate-200 bg-slate-50/80 p-3">
          <summary className="text-sm font-medium text-[#0F2D5C] cursor-pointer">
            Datos para contratos laborales (opcional)
          </summary>
          <p className="text-xs text-muted-foreground mt-2 mb-3">
            Si los deja vacíos, el sistema los solicitará al generar el contrato de un trabajador.
          </p>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2 md:col-span-2">
              <Label>Representante legal</Label>
              <FieldHint>{fieldHints.representante_legal}</FieldHint>
              <Input value={form.representante_legal} onChange={onField("representante_legal")} disabled={!clientId || busy} />
            </div>
            <div className="space-y-2">
              <Label>Cargo del representante</Label>
              <Input value={form.cargo_representante} onChange={onField("cargo_representante")} disabled={!clientId || busy} placeholder="Administrador Único" />
            </div>
            <div className="space-y-2 md:col-span-2 lg:col-span-3">
              <Label>Domicilio del patrón / centro de trabajo</Label>
              <Input value={form.domicilio_patron} onChange={onField("domicilio_patron")} disabled={!clientId || busy} />
            </div>
            <div className="space-y-2">
              <Label>Municipio</Label>
              <Input value={form.municipio_patron} onChange={onField("municipio_patron")} disabled={!clientId || busy} />
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Input value={form.estado_patron} onChange={onField("estado_patron")} disabled={!clientId || busy} />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input value={form.telefono_patron} onChange={onField("telefono_patron")} disabled={!clientId || busy} />
            </div>
            <div className="space-y-2">
              <Label>Correo</Label>
              <Input value={form.correo_patron} onChange={onField("correo_patron")} disabled={!clientId || busy} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Actividad / objeto social</Label>
              <Input value={form.actividad_patron} onChange={onField("actividad_patron")} disabled={!clientId || busy} />
            </div>
          </div>
        </details>

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            onClick={onRegister}
            disabled={!clientId || busy}
            data-testid="btn-registrar-patron"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Registrar Nuevo Patrón
          </Button>
        </div>
      </section>

      {/* Sección inferior — tabla de registros activos */}
      <section className="rounded-lg border bg-card p-4 md:p-6 space-y-3">
        <h3 className="text-sm font-medium">Registros patronales activos</h3>

        {loadingTable ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando registros…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {clientId
              ? "No hay registros patronales para este cliente. Use el formulario superior para dar de alta el primero."
              : "Seleccione un cliente para ver sus registros patronales."}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Registro Patronal</TableHead>
                  <TableHead>Sucursal u Obra</TableHead>
                  <TableHead>Prima de Riesgo</TableHead>
                  <TableHead>Usuario IDSE</TableHead>
                  <TableHead>Certificado</TableHead>
                  <TableHead className="text-right w-[140px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} data-testid={`patron-row-${row.id}`}>
                    <TableCell className="font-mono text-sm">{row.registro_patronal}</TableCell>
                    <TableCell>{row.nombre_sucursal_obra || "—"}</TableCell>
                    <TableCell>{row.prima_riesgo || "—"}</TableCell>
                    <TableCell>{row.usuario_idse || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {row.certificado_fuente === "matriz" ? (
                        <span className="text-emerald-700 font-medium">Matriz (compartido)</span>
                      ) : (
                        <span className="text-slate-600">{row.certificado_pfx_nombre || "Propio"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(row)}
                        disabled={busy}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" />
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteRow(row)}
                        disabled={busy}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Eliminar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
      </div>
      </LaboralFlujoOperativo>

      <Dialog open={Boolean(editRow)} onOpenChange={(open) => !open && setEditRow(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar registro patronal</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1">
              <Label>Registro Patronal</Label>
              <Input value={editForm.registro_patronal} disabled className="font-mono bg-muted" />
            </div>
            <div className="space-y-1">
              <Label>Razón Social IMSS</Label>
              <Input
                value={editForm.razon_social_imss}
                onChange={(e) => setEditForm((p) => ({ ...p, razon_social_imss: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Prima de Riesgo</Label>
              <Input
                value={editForm.prima_riesgo}
                onChange={(e) => setEditForm((p) => ({ ...p, prima_riesgo: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Sucursal / Obra</Label>
              <Input
                value={editForm.nombre_sucursal_obra}
                onChange={(e) => setEditForm((p) => ({ ...p, nombre_sucursal_obra: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Usuario IDSE</Label>
              <Input
                value={editForm.usuario_idse}
                onChange={(e) => setEditForm((p) => ({ ...p, usuario_idse: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Nueva contraseña IDSE (opcional)</Label>
              <Input
                type="password"
                value={editForm.contrasena_idse}
                onChange={(e) => setEditForm((p) => ({ ...p, contrasena_idse: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1">
              <Label>Reemplazar certificado .pfx (opcional)</Label>
              <Input
                ref={editPfxRef}
                type="file"
                accept=".pfx"
                onChange={(e) => setEditPfx(e.target.files?.[0] || null)}
              />
            </div>
            <div className="md:col-span-2 border-t pt-3 mt-1">
              <p className="text-xs font-medium text-[#0F2D5C] mb-2">Datos para contratos (opcional)</p>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Representante legal</Label>
              <Input value={editForm.representante_legal} onChange={(e) => setEditForm((p) => ({ ...p, representante_legal: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Cargo representante</Label>
              <Input value={editForm.cargo_representante} onChange={(e) => setEditForm((p) => ({ ...p, cargo_representante: e.target.value }))} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Domicilio patrón</Label>
              <Input value={editForm.domicilio_patron} onChange={(e) => setEditForm((p) => ({ ...p, domicilio_patron: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Municipio</Label>
              <Input value={editForm.municipio_patron} onChange={(e) => setEditForm((p) => ({ ...p, municipio_patron: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Estado</Label>
              <Input value={editForm.estado_patron} onChange={(e) => setEditForm((p) => ({ ...p, estado_patron: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={onSaveEdit} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteRow)} onOpenChange={(open) => !open && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar registro patronal?</AlertDialogTitle>
            <AlertDialogDescription>
              Se dará de baja el patrón{" "}
              <span className="font-mono font-medium">{deleteRow?.registro_patronal}</span>.
              Esta acción no borra el historial de auditoría en base de datos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ModulePlanBlockedDialog open={planBlockedOpen} onOpenChange={setPlanBlockedOpen} />
    </div>
  );
}
